import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { UserAccess } from "components/user-access/UserAccess";
import { useDebounce } from "hooks/useDebounce";
import { DocumentsUploadModal } from "pages/documents/shared/upload-modal/DocumentUploadModal";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentsService } from "services/DocumentsService";
import { SetFiltering, SetRouting } from "state/actions/Actions";
import { parseBytesTo } from "utils/parseBytesTo";

export function AllDocumentsListing() {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = 'allDocuments';

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const [documentOptions, setDocumentOptions] = useState({
        documentAreas: []
    });

    const tableHeaders = [
        "Filename",
        "Document Type",
        "Version",
        "Document Area",
        "Employee",
        "Asset",
        "Location",
        "Size",
        "Upload Date",
        "Delete Date"
    ];
    const sortableHeaders = [
        { headerName: "Filename", sortName: "ORIGINALFILENAME" },
        { headerName: "Document Type", sortName: "TYPE.NAME" },
        { headerName: "Version", sortName: "VERSION" },
        { headerName: "Document Area", sortName: "TYPE.AREA" },
        { headerName: "Upload Date", sortName: "UPLOADEDON" },
        { headerName: "Size", sortName: "DOCUMENTATIONBYTESIZE" },
        { headerName: "Employee", sortName: "EMPLOYEENAME" },
        { headerName: "Asset", sortName: "ASSETNAME" },
        { headerName: "Location", sortName: "LOCATIONNAME" },
        { headerName: "Delete Date", sortName: "DELETEDDATE" }
    ];

    const [sortedDir, setSortedDir] = useState("D");
    const [sortedBy, setSortedBy] = useState(sortableHeaders.find(s => s.headerName === "Upload Date"));


    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [tableRows, setTableRows] = useState([]);

    const [appliedFilters, setAppliedFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageFilters, setPageFilters] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();

    const selectedFilters = useSelector(state => state.filters[filterName]);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);


    useEffect(() => {
        dispatch(SetRouting(null, null, null));
    }, [])

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []))
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    const onUploadComplete = (anyErrors) => {
        if (anyErrors) {
            NotificationToaster.show(Intent.DANGER, "Some files failed to upload. Please try again.");
        } else {
            NotificationToaster.show(Intent.SUCCESS, "All files uploaded successfully.");
        }

        setUploadModalVisible(false);
        fetchTableData(true);
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentTableSortDir.current === sortedDir &&
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentAppliedFilters.current === appliedFilters &&
                currentSearchTerm.current === debouncedSearchTerm &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentTableSortBy.current = sortedBy;
            currentPageNumber.current = pageNumber;
            currentTableSortDir.current = sortedDir;
            currentAppliedFilters.current = appliedFilters;
            currentSearchTerm.current = debouncedSearchTerm;

            setIsLoading(true);

            const searchRequest = {
                requestCount: defaultRecordSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: (currentAppliedFilters.current ?? []).map(f => {
                    if (f.name === 'DELETEDDATE') {
                        // Get today's date minus 30 days, in ISO format
                        const thirtyDaysAgo = new Date();
                        const thirtyDaysAgoAddition = 30;
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() + thirtyDaysAgoAddition);

                        return {
                            key: f.name,
                            value: thirtyDaysAgo.toISOString(),
                        };

                    } else {
                        return {
                            key: f.name,
                            ...f,
                        };
                    }
                }),
                searchTerm: debouncedSearchTerm,
            };

            DocumentsService.getDocuments(searchRequest).then(
                (r) => {
                    let data = [];

                    if (r?.data != null) {
                        data = r.data.map((d) => {
                            return [
                                <div key={d.id}>{d.featureId ? d.name : <Link to={`/document-settings/documents/${d.id}`}>{`${d.fileName}`}</Link>}</div>,
                                d.type.name,
                                d.version,
                                documentOptions.documentAreas.find(a => a.key === d.type.area)?.name,
                                d.employeeName,
                                d.assetName,
                                d.locationName,
                                parseBytesTo(d.documentationByteSize).auto(),
                                <DateToLocal
                                    key={d.id}
                                    format="DD/MM/YYYY"
                                >{d.uploadedOn}</DateToLocal>,
                                <DateToLocal
                                    key={d.id}
                                    format="DD/MM/YYYY"
                                >{d.deletionDate}</DateToLocal>
                            ];
                        });
                    }

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                    setIsLoading(false);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not retrieve documents, please try again"
                    );
                }
            );
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters
    ]);

    useEffect(() => {
        DocumentsService.getDocumentOptions()
            .then(o => {
                setDocumentOptions(o)
                setPageFilters([
                    {
                        name: 'AREA',
                        displayName: 'Area',
                        items: o.documentAreas.map(a => ({
                            id: a.key,
                            name: a.name
                        }))
                    },
                    {
                        name: 'DELETEDDATE',
                        displayName: 'To Be Deleted In Next 30 Days',
                        items: [
                            { id: 'true', name: 'Yes' }
                        ]
                    }
                ])
                setInitalSetupComplete(true);
            });
    }, []);

    return (
        <>
            <UserAccess perform="Documents:Upload"
                yes={() => <div className="button-row spacer-bottom">
                    <Button
                        text={"Upload Organisation Document"}
                        intent="primary"
                        id="upload-document-button"
                        icon="export"
                        disabled={isLoading}
                        onClick={() => setUploadModalVisible(true)}
                    />
                </div>}
            />
            <div className="filter-controls">
                <div className="inline-items">
                
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search documents"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="document-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                </div>
                <Filter
                        filterName={filterName}
                        filters={pageFilters}
                        visible={showFilters}
                        onUpdate={onFilterChange}
                        onClickOutside={onToggleFilter}
                    />
            </div>
            <ListingTable
                showPerPage={false}
                pageSize={defaultRecordSize}
                headers={tableHeaders}
                data={tableRows}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                totalRecordCount={totalRecords}
                sortable
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                sortableHeaders={sortableHeaders}
                onSort={onTableSort}
            />
            <DocumentsUploadModal
                onClose={() => setUploadModalVisible(false)}
                isOpen={uploadModalVisible}
                entityType={"Organisation"}
                onUploadComplete={onUploadComplete}
                title={"Upload Organisation Document"}
                selectedDocumentTypeName={"Organisational Documents"}
            />
        </>
    );
}